/**
 * Utility functions for handling migrations
 */

/**
 * Compares an original object with its migrated version to determine if migration changed anything
 * @param original The original object before migration
 * @param migrated The object after migration
 * @returns boolean indicating if the objects are different
 */
export function isObjectDifferentAfterMigration<T extends Record<string, any>>(
  original: T,
  migrated: T,
): boolean {
  // Compare all keys in both objects
  const allKeys = Array.from(new Set([...Object.keys(original), ...Object.keys(migrated)]));

  for (const key of allKeys) {
    if (original[key] !== migrated[key]) {
      return true;
    }
  }

  return false;
}

/**
 * Compares arrays of objects to determine if any items were changed by migration
 * @param originalArray The original array before migration
 * @param migratedArray The array after migration
 * @returns boolean indicating if any items were changed
 */
export function isArrayDifferentAfterMigration<T extends Record<string, any>>(
  originalArray: T[],
  migratedArray: T[],
): boolean {
  if (originalArray.length !== migratedArray.length) {
    return true;
  }

  return migratedArray.some((migrated, index) => {
    const original = originalArray[index];
    return isObjectDifferentAfterMigration(original, migrated);
  });
}
